import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Space, Select, Spin, Alert } from 'antd';
import api from '../../utils/axios';

const { TextArea } = Input;
const { Option } = Select;

interface InsuranceRep {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  department?: string;
  insurerId: string;
  status: string;
  notes?: string;
}

interface Insurer {
  id: string;
  name: string;
}

interface InsuranceRepFormProps {
  insuranceRep?: InsuranceRep;
  onSuccess: () => void;
  onCancel: () => void;
}

const InsuranceRepForm: React.FC<InsuranceRepFormProps> = ({
  insuranceRep,
  onSuccess,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [insurers, setInsurers] = useState<Insurer[]>([]);
  const [insurersLoading, setInsurersLoading] = useState(true);

  useEffect(() => {
    fetchInsurers();
  }, []);

  useEffect(() => {
    console.log('Insurers state changed:', insurers.length, 'insurers');
    if (insurers.length > 0) {
      console.log('First 3 insurers:', insurers.slice(0, 3));
    }
  }, [insurers]);

  const fetchInsurers = async () => {
    setInsurersLoading(true);
    try {
      console.log('Fetching insurers...');
      const response = await api.get('/insurers/dropdown');
      console.log('API response received');
      
      let insurersList: Insurer[] = [];
      const responseData = response.data as any;
      if (responseData && responseData.success && responseData.data) {
        insurersList = responseData.data;
        console.log('Found insurers:', insurersList.length);
      } else {
        console.error('Unexpected API response structure:', response.data);
        setError('Invalid API response structure');
        setInsurersLoading(false);
        return;
      }
      
      // Sort alphabetically by name
      insurersList.sort((a, b) => a.name.localeCompare(b.name));
      console.log('Setting insurers state with', insurersList.length, 'items');
      setInsurers(insurersList);
      setInsurersLoading(false);
    } catch (err) {
      console.error('Error fetching insurers:', err);
      setError('Failed to load insurers');
      setInsurersLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setError('');

    try {
      console.log('Form values being submitted:', JSON.stringify(values, null, 2));
      
      // Validate required fields
      if (!values.firstName) {
        setError('First name is required');
        setLoading(false);
        return;
      }
      
      if (!values.insurerId) {
        setError('Insurance company is required');
        setLoading(false);
        return;
      }
      
      console.log('Validation passed, submitting...');
      
      if (insuranceRep) {
        console.log('Updating existing representative:', insuranceRep.id);
        const response = await api.put(`/insurance-reps/${insuranceRep.id}`, values);
        console.log('Update response:', response.data);
      } else {
        console.log('Creating new representative');
        const response = await api.post('/insurance-reps', values);
        console.log('Create response:', response.data);
      }
      onSuccess();
    } catch (err: any) {
      console.error('Submit error details:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Full error object:', JSON.stringify(err, null, 2));
      setError(`Failed to save insurance representative: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={insuranceRep || {
        status: 'ACTIVE',
      }}
      onValuesChange={(changedValues, allValues) => {
        console.log('Form values changed:', allValues);
      }}
    >
      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          name="firstName"
          label="First Name"
          rules={[{ required: true, message: 'Please enter first name' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="lastName"
          label="Last Name"
          rules={[{ required: true, message: 'Please enter last name' }]}
        >
          <Input />
        </Form.Item>
      </div>

      <Form.Item
        name="email"
        label="Email"
        rules={[
          { required: true, message: 'Please enter email' },
          { type: 'email', message: 'Please enter a valid email' },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="phone"
        label="Phone"
      >
        <Input />
      </Form.Item>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          name="title"
          label="Title"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="department"
          label="Department"
        >
          <Input />
        </Form.Item>
      </div>

      <Form.Item
        name="insurerId"
        label="Insurance Company"
        rules={[{ required: true, message: 'Please select insurance company' }]}
      >
        {insurersLoading ? (
          <Spin />
        ) : error ? (
          <Alert type="error" message={error} />
        ) : (
          <select 
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px',
              fontSize: '14px'
            }}
            value={form.getFieldValue('insurerId') || ''}
            onChange={(e) => {
              form.setFieldsValue({ insurerId: e.target.value });
            }}
          >
            <option value="">Select insurance company</option>
            {insurers.map((insurer) => (
              <option key={insurer.id} value={insurer.id}>
                {insurer.name}
              </option>
            ))}
          </select>
        )}
      </Form.Item>

      <Form.Item
        name="notes"
        label="Notes"
      >
        <TextArea rows={3} />
      </Form.Item>

      <Form.Item
        name="status"
        label="Status"
        rules={[{ required: true, message: 'Please select status' }]}
      >
        <select 
          style={{ 
            width: '100%', 
            padding: '8px 12px', 
            border: '1px solid #d9d9d9', 
            borderRadius: '6px',
            fontSize: '14px'
          }}
          value={form.getFieldValue('status') || ''}
          onChange={(e) => {
            console.log('Status selected:', e.target.value);
            form.setFieldsValue({ status: e.target.value });
          }}
        >
          <option value="">Select status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="PENDING">Pending</option>
        </select>
      </Form.Item>

      <Form.Item className="mb-0">
        <div className="flex justify-end space-x-4">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {insuranceRep ? 'Update Representative' : 'Create Representative'}
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};

export default InsuranceRepForm; 